import {
  RECENT_GENERATIONS_STORAGE,
  GENERATION_CONTENT_PREFIX,
} from '../constants/localStorage';
import { HISTORY_ITEMS_EVENT_NAME } from '../constants/recentGenerations';
import { GenerationStatus } from '../enums/generationStatus';
import { PageKey } from '../enums/pageKey';
import { Portal } from '../enums/portal.enum';
import { RecentGenerationMetadata } from '../interfaces/recentGeneration';
import { FormContents } from '../types/page';

export interface GenerationContent {
  id: string;
  content: string | Record<string, unknown>;
  generatedAt: number;
  status: GenerationStatus;
  inputs?: FormContents;
}

export class RecentGenerationsStorage {
  private static instance: RecentGenerationsStorage;

  static getInstance(): RecentGenerationsStorage {
    if (!this.instance) {
      this.instance = new RecentGenerationsStorage();
    }
    return this.instance;
  }

  private safeParseJSON<T>(jsonString: string, fallback: T): T {
    try {
      const parsed = JSON.parse(jsonString);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  private dispatchHistoryItemsEvent(): void {
    if (typeof window !== 'undefined') {
      queueMicrotask(() => {
        window.dispatchEvent(new Event(HISTORY_ITEMS_EVENT_NAME));
      });
    }
  }

  storeGeneration(
    pageKey: PageKey,
    portal: Portal,
    content: string | Record<string, unknown>,
    status: GenerationStatus = GenerationStatus.COMPLETE,
    existingId?: string,
    subject?: string,
    inputs?: FormContents,
  ): { success: boolean; id?: string } {
    if (typeof window === 'undefined') {
      return { success: false };
    }

    try {
      const id = existingId || this.generateId();
      let timestamp = Date.now();
      let originalGeneratedAt = timestamp;
      let existingInputs = inputs;

      if (existingId) {
        const currentList = this.getRecentGenerationsList();
        const existingItem = currentList.find((item) => item.id === existingId);
        if (existingItem) {
          timestamp = existingItem.timestamp; // Preserve original timestamp
        }

        const existingContentKey = `${GENERATION_CONTENT_PREFIX}${existingId}`;
        const existingContentStr = localStorage.getItem(existingContentKey);
        if (existingContentStr) {
          const existingContent = this.safeParseJSON<GenerationContent>(
            existingContentStr,
            null,
          );
          if (existingContent) {
            originalGeneratedAt = existingContent.generatedAt;
            existingInputs = inputs || existingContent.inputs;
          }
        }
      }

      const generationContent: GenerationContent = {
        id,
        content,
        generatedAt: originalGeneratedAt,
        status,
        inputs: existingInputs,
      };

      localStorage.setItem(
        `${GENERATION_CONTENT_PREFIX}${id}`,
        JSON.stringify(generationContent),
      );

      const metadata: RecentGenerationMetadata = {
        id,
        pageKey,
        portal: portal.toString(),
        timestamp,
        status,
        subject,
      };

      this.addToRecentGenerationsList(metadata);
      this.dispatchHistoryItemsEvent();

      return { success: true, id };
    } catch (error) {
      console.error('Error storing generation:', error);
      return { success: false };
    }
  }

  updateGenerationStatus(id: string, status: GenerationStatus): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      // Update the content with new status
      const contentKey = `${GENERATION_CONTENT_PREFIX}${id}`;
      const existingContent = localStorage.getItem(contentKey);

      if (existingContent) {
        const content = this.safeParseJSON<GenerationContent>(
          existingContent,
          null,
        );
        if (!content) {
          console.error('Invalid generation content found for id:', id);
          return false;
        }

        content.status = status;
        localStorage.setItem(contentKey, JSON.stringify(content));
      }

      // Update the metadata
      const currentList = this.getRecentGenerationsList();
      const updatedList = currentList.map((item) =>
        item.id === id ? { ...item, status } : item,
      );

      try {
        localStorage.setItem(
          RECENT_GENERATIONS_STORAGE.recentGenerationsList,
          JSON.stringify(updatedList),
        );
      } catch (error) {
        console.error('Error updating local storage:', error);
        return false;
      }

      this.dispatchHistoryItemsEvent();

      return true;
    } catch (error) {
      console.error('Error updating generation status:', error);
      return false;
    }
  }

  private addToRecentGenerationsList(metadata: RecentGenerationMetadata): void {
    const currentList = this.getRecentGenerationsList();
    const existingIndex = currentList.findIndex(
      (item) => item.id === metadata.id,
    );

    let updatedList: RecentGenerationMetadata[];

    if (existingIndex >= 0) {
      // Update existing entry in place
      updatedList = [...currentList];
      updatedList[existingIndex] = metadata;
    } else {
      // Add new entry at the beginning
      updatedList = [metadata, ...currentList];
    }

    const maxSize = RECENT_GENERATIONS_STORAGE.maxRecentGenerations;
    const willExceedLimit = updatedList.length > maxSize;
    const trimmedList = updatedList.slice(0, maxSize);
    if (willExceedLimit) {
      const trimmedIds = new Set(trimmedList.map((item) => item.id));
      const updatedIds = new Set(updatedList.map((item) => item.id));
      const staleIds = [...updatedIds].filter((id) => !trimmedIds.has(id));
      staleIds.forEach((id) => {
        localStorage.removeItem(`${GENERATION_CONTENT_PREFIX}${id}`);
      });
    }

    localStorage.setItem(
      RECENT_GENERATIONS_STORAGE.recentGenerationsList,
      JSON.stringify(trimmedList),
    );
  }

  private getRecentGenerationsList(): RecentGenerationMetadata[] {
    if (typeof window === 'undefined') return [];

    try {
      const stored = localStorage.getItem(
        RECENT_GENERATIONS_STORAGE.recentGenerationsList,
      );
      return stored
        ? this.safeParseJSON<RecentGenerationMetadata[]>(stored, [])
        : [];
    } catch (error) {
      console.error('Error getting recent generations list:', error);
      return [];
    }
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `gen_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
}
